/** biome-ignore-all lint/suspicious/noExplicitAny: This imports old code that isn't `any`-safe. */
import { type DescService, hasOption } from "@bufbuild/protobuf";
import type { Interceptor } from "@connectrpc/connect";
import { type Client, createClient, type Transport } from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
import * as t from "io-ts";
import { Payload } from "#root/lib/crypto";
import { autoencode } from "#root/lib/format";
import { iotsValidateThrow, tSodiumBytes } from "#root/lib/iots";
import { BaseService } from "#root/proto/base_pb";
import { XXX_THINK_CAREFULLY_no_token } from "#root/proto/options_pb";
import { SyncedBrowserStorage } from "./storage";
import { v4 } from "uuid";
import type * as internal from "./types";

interface Credentials {
  username: string;
  userID: string;
  privateKey: Uint8Array;
  publicKey: Uint8Array;
  serverPublicKey: Uint8Array;
}

const DEFAULT_UNKNOWN_CREDENTIALS: Credentials = {
  username: undefined,
  userID: undefined,
  privateKey: undefined,
  publicKey: undefined,
  serverPublicKey: undefined,
};

export class ClientBase {
  protected baseURL: string;
  protected transport: Transport;

  protected credentials = new SyncedBrowserStorage<Credentials>(
    "clientbase",
    DEFAULT_UNKNOWN_CREDENTIALS,
  );

  constructor(baseURL = "") {
    this.baseURL = baseURL;
    this.transport = createConnectTransport({
      baseUrl: baseURL,
      interceptors: [this.authInterceptor],
    });
  }

  /**
   * @returns a client with the correct base URL and any additional credentials.
   */
  protected useClient<T extends DescService>(service: T): Client<T> {
    return createClient(service, this.transport);
  }

  /**
   * @returns an interceptor that can automatically add token fetching to requests.
   */
  private authInterceptor: Interceptor = (next) => async (req) => {
    if (!hasOption(req.method, XXX_THINK_CAREFULLY_no_token)) {
      // XXX: if BaseService.GetToken does not have XXX_THINK_CAREFULLY_no_token, this will make an infinite loop. :)
      const newToken = await this.getToken(req.method.name);
      req.header.set("Callisto-Token", newToken);
      req.header.set("X-Callisto-Token", newToken);
    }
    return await next(req);
  };

  // Tokens ////////////////////////////////////////////////////////////////////
  private async getToken(operation?: string): Promise<string> {
    const client = this.useClient(BaseService);
    const resp = await client.getToken({ operation });

    return resp.token;
  }

  // TODO: rework, just, all of this.
  // Original Client ///////////////////////////////////////////////////////////
  get username(): string {
    return this.credentials.get().username;
  }
  set username(value: string) {
    this.credentials.set({ ...this.credentials.get(), username: value });
  }

  get userID(): string {
    return this.credentials.get().userID;
  }
  set userID(value: string) {
    this.credentials.set({ ...this.credentials.get(), userID: value });
  }

  // Unprotected for testing
  get privateKey(): Uint8Array {
    return this.credentials.get().privateKey;
  }
  set privateKey(value: Uint8Array) {
    this.credentials.set({ ...this.credentials.get(), privateKey: value });
  }

  // Open for testing
  get publicKey(): Uint8Array {
    return this.credentials.get().publicKey;
  }
  set publicKey(value: Uint8Array) {
    this.credentials.set({ ...this.credentials.get(), publicKey: value });
  }

  get serverKey(): Uint8Array {
    return this.credentials.get().serverPublicKey;
  }

  set serverKey(value: Uint8Array) {
    this.credentials.set({ ...this.credentials.get(), serverPublicKey: value });
  }

  get isLoggedIn(): boolean {
    return (
      this.credentials.get() !== undefined &&
      this.username !== undefined &&
      this.userID !== undefined &&
      this.privateKey !== undefined &&
      this.publicKey !== undefined &&
      this.serverKey !== undefined
    );
  }

  /**
   * call is a thin wrapper over rawRequest that just always does JSON
   * decoding.
   **/
  protected call<
    I extends t.Type<any, any, any>,
    O extends t.Type<any, any, any>,
  >(
    decl: internal.RPCFunction<I, O>,
  ): (args: t.TypeOf<I>) => Promise<t.TypeOf<O>> {
    return async (params: t.TypeOf<I>): Promise<t.TypeOf<O>> => {
      const {
        name: rpcName,
        i: input,
        o: output,
        XXX_THINK_CAREFULLY_no_token: skipToken,
      } = decl();
      const resp = await this.rawCall(rpcName, skipToken, input.encode(params));

      if (resp.status !== 200) {
        throw await resp.json();
      }

      return iotsValidateThrow(output, await resp.json()) as (
        args: t.TypeOf<I>,
      ) => Promise<t.TypeOf<O>>;
    };
  }

  // encryptedRequest encrypts information between client/server in a way
  // that allows the client to prove their identity on each request.
  // protected encryptedCall<I, O>(decl: internal.RPCFunction<I, O>): (args: I) => Promise<O> {
  protected encryptedCall<
    I extends t.Type<any, any, any>,
    O extends t.Type<any, any, any>,
  >(
    decl: internal.RPCFunction<I, O>,
  ): (args: t.TypeOf<I>) => Promise<t.TypeOf<O>> {
    return async (params: t.TypeOf<I> | void): Promise<t.TypeOf<O>> => {
      const {
        name: rpcName,
        i: input,
        o: output,
        XXX_THINK_CAREFULLY_no_token: skipToken,
      } = decl();
      // Payloads can't be undefined, so we create a blank object and send it
      // across the wire. This still helps us do verification of data on the
      // wire.
      const a = input.encode(params ?? {}) as Uint8Array;
      const encryptedBody = Payload.encrypt(a, this.serverKey, this.privateKey);

      const tInput = t.type({
        id: t.string,
        payload: tSodiumBytes,
      });
      const tOutput = t.type({
        payload: tSodiumBytes,
      });

      const resp = await this.rawCall(
        rpcName,
        skipToken,
        tInput.encode({
          payload: encryptedBody,
          id: this.userID,
        }),
      );

      if (resp.status === 200) {
        const { payload: body } = iotsValidateThrow(tOutput, await resp.json());

        const decrypted = Payload.decryptBasic(
          body,
          this.serverKey,
          this.privateKey,
        ) as unknown;

        return iotsValidateThrow(output, decrypted) as (
          args: t.TypeOf<I>,
        ) => Promise<t.TypeOf<O>>;
      }
      throw await resp.json();
    };
  }

  /**
   * rawCall sets some common options for all requests that our
   * application does.
   *
   * @param rpcName Name of function to call.
   * @param skipToken Avoid fetching a token before making this call.
   * @param data Any additional data to send in the request body. Will be JSON-encoded.
   */
  protected async rawCall(
    rpcName: string,
    skipToken: unknown,
    data: any = {},
  ): Promise<Response> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (!skipToken) {
      const token = await this.getToken(rpcName);
      headers["X-Callisto-Token"] = token; // TODO: remove this
      headers["Callisto-Token"] = token;
    }

    const options: RequestInit = {
      body: JSON.stringify(data),
      method: "POST",
      headers,
      mode: "same-origin",
      cache: "no-cache",
      credentials: "same-origin",
      redirect: "follow",
      referrerPolicy: "no-referrer",
    };

    return fetch(`${this.baseURL}/api/v1/rpc/${rpcName}`, options);
  }

  /**
   * rawRequest sets some common options for all requests that our
   * application does.
   *
   * @param path API path to access.
   * @param data Any additional data to send in the request body. Will be JSON-encoded.
   * @param additionalHeaders Additional headers to include beyond JSON.
   */
  protected async rawRequest(
    path: string,
    data: any = {},
    additionalHeaders?: Record<string, string>,
  ): Promise<Response> {
    const options: RequestInit = {
      body: JSON.stringify(autoencode(data)),
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(additionalHeaders ?? {}),
      },
      mode: "same-origin",
      cache: "no-cache",
      credentials: "same-origin",
      redirect: "follow",
      referrerPolicy: "no-referrer",
    };

    return fetch(`${this.baseURL}${path}`, options);
  }
}
